import { supabase } from '../lib/supabase'
import { AircraftOverride, AircraftOverrideInput } from '../types/database'
import { OverrideHistoryService, OverrideSource } from './override-history.service'

export class OverridesService {
  /**
   * Get override data for a specific aircraft
   */
  static async getByAircraftId(aircraftId: number): Promise<AircraftOverride | null> {
    try {
      const { data, error } = await supabase
        .from('ac_overrides')
        .select('*')
        .eq('aircraft_id', aircraftId)
        .maybeSingle()

      if (error) {
        throw error
      }
      return data
    } catch (error) {
      throw error
    }
  }

  /**
   * Get override data for multiple aircraft
   */
  static async getByAircraftIds(aircraftIds: number[]): Promise<Map<number, AircraftOverride>> {
    try {
      if (!aircraftIds || aircraftIds.length === 0) {
        return new Map<number, AircraftOverride>()
      }

      const { data, error } = await supabase
        .from('ac_overrides')
        .select('*')
        .in('aircraft_id', aircraftIds)

      if (error) {
        throw error
      }

      const overrideMap = new Map<number, AircraftOverride>()
      if (data && Array.isArray(data)) {
        data.forEach(override => {
          if (override && override.aircraft_id) {
            overrideMap.set(override.aircraft_id, override)
          }
        })
      }

      return overrideMap
    } catch (error) {
      throw error
    }
  }

  /**
   * Create or update override data for an aircraft
   */
  static async upsert(overrideData: AircraftOverrideInput): Promise<AircraftOverride> {
    try {
      // Check if override already exists
      const existing = await this.getByAircraftId(overrideData.aircraft_id)

      if (existing) {
        // Update existing override
        const { data, error } = await supabase
          .from('ac_overrides')
          .update(overrideData)
          .eq('aircraft_id', overrideData.aircraft_id)
          .select()
          .single()

        if (error) {
          throw error
        }

        return data
      } else {
        // Create new override
        const { data, error } = await supabase
          .from('ac_overrides')
          .insert({
            ...overrideData,
            created_by: (await supabase.auth.getUser()).data.user?.id
          })
          .select()
          .single()

        if (error) {
          throw error
        }

        return data
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Update a single field on an aircraft override with history logging.
   * This is the preferred method for inline editing as it logs changes.
   * @param aircraftId - The aircraft ID
   * @param fieldName - The field name to update (must be a valid AircraftOverride field)
   * @param newValue - The new value for the field
   * @param source - The source of the change (defaults to 'inline_edit')
   */
  static async updateField(
    aircraftId: number,
    fieldName: keyof AircraftOverrideInput,
    newValue: unknown,
    source: OverrideSource = 'inline_edit'
  ): Promise<AircraftOverride> {
    try {
      // Get existing override to capture old value
      const existing = await this.getByAircraftId(aircraftId)
      const oldValue = existing ? (existing as unknown as Record<string, unknown>)[fieldName] : null

      // Build the update data
      const updateData: AircraftOverrideInput = {
        aircraft_id: aircraftId,
        [fieldName]: newValue
      }

      // Perform the upsert
      const result = await this.upsert(updateData)

      // Log the change to history (don't await - fire and forget)
      OverrideHistoryService.logAircraftOverrideChange({
        aircraftId,
        fieldName: fieldName as string,
        oldValue,
        newValue,
        source
      }).catch(() => { /* Failed to log history */ })

      return result
    } catch (error) {
      throw error
    }
  }

  /**
   * Delete override data for an aircraft
   */
  static async delete(aircraftId: number): Promise<void> {
    try {
      const { error } = await supabase
        .from('ac_overrides')
        .delete()
        .eq('aircraft_id', aircraftId)

      if (error) {
        throw error
      }
    } catch (error) {
      throw error
    }
  }

  /**
   * Get all overrides (for admin purposes)
   */
  static async getAll(): Promise<AircraftOverride[]> {
    try {
      const { data, error } = await supabase
        .from('ac_overrides')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      return data || []
    } catch (error) {
      throw error
    }
  }
}
